<template>
  <div :class="$style.sectionRoot">
    <MessageContainer
      v-if="isInBadState"
      level="error"
    >
      <h3 :class="$style.messageTitle">
        macOS Issue: Service Account Invalid
      </h3>
      JumpCloud is not installed correctly on this device.
      <ExternalLink
        v-if="secureTokenAdminList.length === 0"
        data-test-id="learn-more-link"
        :href="KbArticleUrls.usingSecureTokens"
        :showExternalLinkIcon="false"
        text="Learn More"
      />
      <ButtonLink
        v-else
        data-test-id="admin-list"
        text="View admin list"
        @click="handleViewAdminList"
      />
      <div
        v-for="(trueOrFalseState, serviceAccountDetail) in getServiceAccountState"
        :key="serviceAccountDetail"
        :class="$style.serviceAccountStateSection"
      >
        <p
          :class="$style.serviceAccountStateRow"
          data-test-id="DeviceDetails_serviceAccountStateRow"
        >
          {{ serviceAccountDetail }} <strong>{{ capitalize(trueOrFalseState) }}</strong>
        </p>
      </div>
    </MessageContainer>
    <h2 :class="$style.sectionHeader">
      {{ osDisplayName }} {{ deviceItem.title }} Details
    </h2>
    <div :class="$style.sectionContent">
      <div :class="$style.inputRow">
        <div :class="$style.flexSingleWidth">
          <InputValidator
            v-model="deviceItem.displayName"
            :config="{
              attribute: 'displayName',
              label: `${isIOS ? 'JumpCloud ' : ''}${deviceItem.title} Name`,
              name: `${deviceItem.title} Name`,
              value: item.displayName,
            }"
            @InputValidatorModel="handleValueChange"
          />
        </div>
      </div>
      <div :class="$style.inputRow">
        <div :class="$style.flexSingleWidth">
          <TextareaValidator
            v-model="deviceItem.description"
            :config="{
              attribute: 'description',
              label: 'Description',
              name: 'Description',
              validate: 'max:1024',
              value: item.description,
            }"
            @InputValidatorModel="handleValueChange"
          />
        </div>
      </div>
      <div
        v-for="(row, index) in readonlyFields"
        :key="index"
        :class="$style.inputRow"
      >
        <div
          v-for="field in row"
          :key="field.label"
          :class="$style.flexSingleWidth"
        >
          <InputValidator
            :class="[$style.textInput, $style.iOSLabel]"
            :config="{
              label: field.label,
              name: field.label,
              readonly: true,
              value: field.value || unknown,
            }"
            :data-test-id="`row`"
            :tooltip="field.tooltip"
          />
        </div>
      </div>
      <div
        v-if="isFdeKeyPresent"
        :class="$style.inputRow"
      >
        <div :class="$style.flexSingleWidth">
          <label
            :class="[
              $style.label,
              $style['label-position'],
              $style['label-font'],
              $style['label-color'],
              $style['label-typography'],
            ]"
          >
            Recovery Key
          </label>
          <ViewRecoveryKeyButton
            :item="item"
            :showLink="true"
          />
        </div>
      </div>
      <div
        v-if="!isIOS"
        :class="$style.inputRow"
      >
        <div :class="[$style.flexSingleWidth, $style.systemAgentLogs]">
          <SystemAgentLogs />
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { capitalize, get } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import AsidePanelChildComponentMethodsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentMethodsMixin';
import AsidePanelChildComponentPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelChildComponentPropsMixin';
import ButtonLink from 'client/components/Buttons/ButtonLink.vue';
import Constants, { ItemTypeConsts, StoreModelMap } from 'client/util/Constants';
import ExternalLink from 'components/ExternalLink.vue';
import InputValidator from 'client/components/FormValidation/InputValidator.vue';
import ItemType from 'client/util/Constants/ItemType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';
import ModalEventBus from 'client/components/Modal/ModalEventBus';
import SystemAgentLogs from 'client/systems/SystemAgentLogs.vue';
import TextareaValidator from 'client/components/FormValidation/TextareaValidator.vue';
import ViewRecoveryKeyButton from 'client/components/ItemTable/BodyComponents/ViewRecoveryKeyButton.vue';
import moment from 'moment';

const storeModel = StoreModelMap[ItemTypeConsts.system];
const {
  linux,
  iOS,
  macOS,
  unknown,
  windows,
} = Constants;

const { DeviceItemType } = ItemType;

export default {
  name: 'DeviceDetails',

  inject: ['$validator'],

  components: {
    ButtonLink,
    ExternalLink,
    InputValidator,
    MessageContainer,
    SystemAgentLogs,
    TextareaValidator,
    ViewRecoveryKeyButton,
  },

  mixins: [
    AsidePanelChildComponentMethodsMixin,
    AsidePanelChildComponentPropsMixin,
  ],

  props: {
    handleValueChange: {
      type: Function,
      required: true,
    },
  },

  data: () => ({
    capitalize,
    deviceItem: new DeviceItemType(),
    KbArticleUrls,
    unknown,
  }),

  computed: {
    ...mapGetters(storeModel, [
      'active',
      'fdeDescriptionVerbose',
      'getServiceAccountState',
      'getSystemNIC',
      'isFdeKeyPresent',
      'isLinux',
      'isIOS',
      'isInBadState',
      'isMacOS',
      'isWindows',
      'osWithVersion',
      'secureTokenAdminList',
      'showSystemInsights',
    ]),

    ...mapState(storeModel, {
      item: state => state.item,
    }),

    /**
     * @todo Move this to a getter in the store if this logic is present elsewhere!
     */
    osDisplayName() {
      let osName = '';

      if (this.isLinux) {
        osName = linux;
      }

      if (this.isIOS) {
        osName = iOS;
      }

      if (this.isMacOS) {
        osName = macOS;
      }

      if (this.isWindows) {
        osName = windows;
      }

      return osName;
    },

    readonlyFields() {
      const {
        _id,
        agentVersion,
        amazonInstanceID,
        arch,
        deviceInformation = {},
        hostname,
        id,
        lastContact,
        mdm: { internal: { deviceId = '' } = {} },
        osFamily,
        osVersion,
        remoteIP,
        securityInfo = {},
        serialNumber,
      } = this.item;
      const lastContactDate = moment(lastContact).utc().fromNow();
      let storageUsage = unknown;
      const deviceCapacity = get(deviceInformation, 'deviceCapacity', false);
      const availableDeviceCapacity = get(deviceInformation, 'availableDeviceCapacity', false);
      const dualSimDetected = get(deviceInformation, 'secondImei', false);

      if (deviceCapacity && availableDeviceCapacity) {
        const storageDifference = (deviceCapacity - availableDeviceCapacity).toFixed(2);
        storageUsage = `${storageDifference}GB / ${deviceCapacity}GB`;
      }
      let fields = [];
      const { deviceItem } = this;

      if (this.isMacOS) {
        fields = [
          [
            { value: this.active, attribute: 'active', label: 'Status' },
            { value: lastContactDate, attribute: 'lastContact', label: 'Last Contact' },
          ],
          [
            { value: hostname, attribute: 'hostname', label: 'Host Name' },
            { value: arch, attribute: 'arch', label: 'Architecture' },
          ],
          [
            { value: this.osWithVersion, attribute: 'os', label: 'Operating System' },
            { value: remoteIP, attribute: 'remoteIP', label: 'Remote IP Address' },
          ],
          [
            { value: this.getSystemNIC, attribute: '', label: 'Primary Adapter IP' },
            { value: agentVersion, attribute: 'agentVersion', label: 'JC Agent Version' },
          ],
          [
            { value: serialNumber, attribute: 'serialNumber', label: 'Serial Number' },
            {
              value: this.fdeDescriptionVerbose, attribute: '', label: 'Full Disk Encryption Status',
            },
          ],
          [
            { value: id, attribute: 'id', label: `JumpCloud ${deviceItem.title} ID` },
            { value: deviceId, attribute: 'deviceId', label: `MDM ${deviceItem.title} ID` },
          ],
        ];
      } else if (this.isIOS) {
        fields = [
          [
            { value: deviceInformation.deviceName, attribute: 'deviceName', label: 'iOS Device Name' },
            { value: `${osFamily} ${osVersion}`, attribute: 'osFamily', label: 'Operating System' },
          ],
          [
            { value: deviceInformation.modelName, attribute: 'model', label: 'Model' },
            { value: serialNumber, attribute: 'serialNumber', label: 'Serial Number' },
          ],
          [
            { value: lastContactDate, attribute: 'lastContact', label: 'Last Contact' },
            { value: _id, attribute: 'id', label: 'JumpCloud System ID' },
          ],
          [
            { value: deviceId, attribute: 'deviceId', label: `MDM ${deviceItem.title} ID` },
            { value: deviceInformation.wifiMac, attribute: '', label: 'Wifi MAC' },
          ],
          [
            { value: securityInfo.passcodePresent, attribute: 'passcodePresent', label: 'Passcode Present' },
          ],
          [
            { value: deviceInformation.subscriberCarrierNetwork, attribute: '', label: 'Carrier' },
          ],
          [
            { value: deviceInformation.imei, attribute: '', label: 'IMEI' },
            { value: deviceInformation.iccid, attribute: '', label: 'ICCID' },
          ],
          [
            { value: `${deviceInformation.isSupervised ? 'Supervised' : 'Unsupervised'}`, attribute: '', label: 'Supervision Status' },
            { value: storageUsage, attribute: '', label: 'Storage Usage / Capacity' },
          ],
        ];
      } else {
        fields = [
          [
            { value: this.active, attribute: 'active', label: 'Status' },
            { value: lastContactDate, attribute: 'lastContact', label: 'Last Contact' },
          ],
          [
            { value: hostname, attribute: 'hostname', label: 'Host Name' },
            { value: amazonInstanceID, attribute: 'amazonInstanceID', label: 'Instance ID' },
          ],
          [
            { value: this.osWithVersion, attribute: 'os', label: 'Operating System' },
            { value: arch, attribute: 'arch', label: 'Architecture' },
          ],
          [
            { value: this.getSystemNIC, attribute: '', label: 'Primary Adapter IP' },
            { value: remoteIP, attribute: 'remoteIP', label: 'Remote IP Address' },
          ],
          [
            { value: serialNumber, attribute: 'serialNumber', label: 'Serial Number' },
            { value: agentVersion, attribute: 'agentVersion', label: 'JC Agent Version' },
          ],
          [
            { value: id, attribute: 'id', label: `JumpCloud ${deviceItem.title} ID` },
            {
              value: this.fdeDescriptionVerbose, attribute: '', label: 'Full Disk Encryption Status', tooltip: { text: 'For more information on the Encryption Status of all drives, see System Insights.' },
            },
          ],
        ];
      }

      if (this.showSystemInsights) fields = [fields[0]];

      if (this.isIOS && dualSimDetected) {
        const carrierField = fields.find(arr => arr[0].label === 'Carrier');
        carrierField[0].label = 'Carrier (SIM 1)';
        const supervisionStatusRow = fields.findIndex(arr => arr[0].label === 'Supervision Status');
        fields.splice(supervisionStatusRow, 0, [
          { value: deviceInformation.secondSubscriberCarrierNetwork, attribute: '', label: 'Carrier (SIM 2)' },
        ],
        [
          { value: deviceInformation.secondImei, attribute: '', label: 'IMEI' },
          { value: deviceInformation.secondIccid, attribute: '', label: 'ICCID' },
        ]);
      }

      return fields;
    },
  },

  beforeCreate() {
    this.$options.methods.asidePanelChildComponentHandleBeforeCreate(this.$options);
  },

  methods: {
    handleViewAdminList() {
      let mdTemplate = '';
      let adminList = '';

      this.secureTokenAdminList.forEach((admin) => {
        adminList += `\n\n${admin.userName}`;
      });
      mdTemplate = `A secure token allows users to perform critical operations on a macOS device. Contact any of these admins to repair the invalid state of this device.${adminList}`;

      ModalEventBus.open({
        component: 'ConfirmationModal',
        componentProps: {
          mdTemplate,
          cancelText: '',
          continueText: 'ok',
          headerText: 'Admins With Secure Tokens',
          kbLink: KbArticleUrls.usingSecureTokens,
          showClose: true,
        },
      });
    },
  },
};
</script>
<style module>
@import '../css/sections.css';
@import '../css/flex.css';
@import "../components/FormValidation/css/formFields.css";

.iOSLabel label {
  text-transform: initial;
}

.messageTitle {
  display: inline;
  font-size: 1em;
}

.systemAgentLogs {
  margin-top: 1em;
}

.serviceAccountStateRow {
  display: flex;
  justify-content: space-between;
  max-width: 15.25rem;
}

.serviceAccountStateSection {
  margin-top: 0.5rem;
}

.textInput input[readonly],
.textInput input[readonly]:focus {
  background-color: white;
  border-color: transparent;
  opacity: inherit !important;
  overflow: hidden;
  padding-left: 0;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
